/**
 * FollowupBadge - Shows active follow-up indicator in the chat header.
 * Compact badge that expands to show details with cancel/view actions.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Calendar, X, ExternalLink, Loader2, Trash2, Clock, Repeat } from 'lucide-react';
import followupService from '../../services/global/followupService';

const RECURRENCE_LABELS = {
  once: 'Envio único',
  daily: 'Diário',
  weekly: 'Semanal',
  monthly: 'Mensal',
};

const WEEKDAY_NAMES = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

/** Format YYYY-MM-DD to DD/MM/YYYY */
const formatDateBR = (dateStr) => {
  if (!dateStr || dateStr.length !== 10) return dateStr || '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};

const buildScheduleText = (schedule) => {
  if (!schedule) return '';
  const time = schedule.send_time || '09:00';
  const rec = schedule.recurrence || 'once';

  if (rec === 'once') {
    const dateStr = formatDateBR(schedule.send_date || schedule.start_date || '');
    return `Uma vez às ${time}${dateStr ? ` em ${dateStr}` : ''}`;
  }
  if (rec === 'daily') return `Diário às ${time}`;
  if (rec === 'weekly') {
    const days = (schedule.recurrence_days || []).map(d => WEEKDAY_NAMES[d] || '').filter(Boolean);
    return `Semanal ${days.join('/')} às ${time}`;
  }
  if (rec === 'monthly') {
    const day = schedule.recurrence_day_of_month || 1;
    return `Mensal dia ${day} as ${time}`;
  }
  return `${RECURRENCE_LABELS[rec] || rec} as ${time}`;
};

/** Texto em português do último desfecho registrado pelo scheduler
 *  (followups "once": por que não saiu). null quando não há o que mostrar. */
const describeOutcome = (outcome) => {
  if (!outcome || !outcome.status || outcome.status === 'sent' || outcome.status === 'pending') return null;
  const SKIP_LABELS = {
    skipped_human_attendance: 'Não enviado: contato em atendimento humano (marque "forçar resposta" para enviar mesmo assim)',
    skipped_24h_window: 'Não enviado: janela de 24h fechada',
    skipped_by_agent: 'Não enviado: o agente avaliou e decidiu não enviar',
    skipped_ownership: 'Não enviado: contato transferido para outro workspace',
    cancelled_on_reply: 'Cancelado: o cliente respondeu antes do envio',
  };
  if (SKIP_LABELS[outcome.status]) return SKIP_LABELS[outcome.status];
  const FAIL_LABELS = {
    no_active_agent: 'Falhou: o workspace não tem agente de IA ativo',
    agent_timeout: 'Falhou: a geração da mensagem pela IA excedeu o tempo',
    empty_response: 'Falhou: a IA devolveu resposta vazia',
    missing_instruction: 'Falhou: a mensagem não tem instrução para a IA',
  };
  const err = outcome.error ? String(outcome.error) : '';
  if (FAIL_LABELS[err]) return FAIL_LABELS[err];
  return `Falhou: ${err || 'erro no envio'}`;
};

const FollowupBadge = ({ contactPhone, colors, onNavigateToFollowup, refreshKey }) => {
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [removing, setRemoving] = useState(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!contactPhone) return;
    let cancelled = false;
    setLoading(true);
    followupService.getByRecipient(contactPhone)
      .then(res => {
        if (!cancelled) setFollowups(res?.data || []);
      })
      .catch(() => {
        if (!cancelled) setFollowups([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [contactPhone, refreshKey]);

  // Click outside to close
  useEffect(() => {
    if (!expanded) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [expanded]);

  const handleRemove = async (followupId) => {
    if (!window.confirm('Cancelar este agendamento para este contato?')) return;
    setRemoving(followupId);
    try {
      await followupService.removeRecipient(followupId, contactPhone);
      setFollowups(prev => {
        const remaining = prev.filter(f => f.id !== followupId);
        if (remaining.filter(f => !(f.recurrence === 'once' && f.already_sent)).length === 0) {
          setExpanded(false);
        }
        return remaining;
      });
    } catch (err) {
      alert('Erro ao cancelar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRemoving(null);
    }
  };

  // Filter: hide "once" followups that were already sent
  const activeFollowups = followups.filter(f => !(f.recurrence === 'once' && f.already_sent));

  if (loading || activeFollowups.length === 0) return null;

  // VENCIDO: um agendamento "once" cuja data/hora já passou e que NÃO foi
  // enviado (o backend não tem estado "expirado" — ver followup_scheduler).
  // Em vez de ficar como "agendado" verde para sempre, refletimos como
  // "Vencido" (âmbar) para o operador saber que não saiu e poder agir.
  const OVERDUE = '#f59e0b';
  const isOverdue = (f) => {
    if (f.recurrence !== 'once') return false;
    // Backend autoritativo: marcou 'expired' (dia passou sem envio).
    if (f.expired) return true;
    // Fallback client-side (janela entre vencer e o próximo tick do scheduler).
    const d = f.schedule?.send_date || f.schedule?.start_date;
    if (!d) return false;
    const t = f.schedule?.send_time || '23:59';
    const when = new Date(`${d}T${t}:00`);
    if (isNaN(when.getTime())) return false;
    return when.getTime() < Date.now();
  };
  const overdueCount = activeFollowups.filter(isOverdue).length;
  const pendingCount = activeFollowups.length - overdueCount;
  // Cor do ícone: âmbar quando SÓ há vencidos; verde quando há algum pendente.
  const accent = pendingCount > 0 ? colors.accentPrimary : OVERDUE;

  // Skip terminal registrado pelo scheduler (ex.: contato em atendimento
  // humano): não é "vencido" nem "agendado" — mostra o motivo.
  const isSkipped = (f) =>
    f.recurrence === 'once' &&
    String(f.last_outcome?.status || '').startsWith('skipped');

  // Build tooltip text from active followups
  const tooltipText = activeFollowups
    .map(f => {
      const reason = describeOutcome(f.last_outcome);
      const suffix = isOverdue(f)
        ? ` (vencido — ${reason || 'não enviado'})`
        : (reason ? ` (${reason})` : '');
      return `${f.name}: ${buildScheduleText(f.schedule)}${suffix}`;
    })
    .join('\n');

  return (
    <div ref={panelRef} style={{ position: 'relative', display: 'inline-flex' }}>
      {/* Botão-ícone (estilo do toggle de agente): só o calendário, compacto.
          Com mais de um agendamento, um contador aparece sobre o ícone — a
          lista completa continua no painel ao clicar. Âmbar = vencido. */}
      <button
        onClick={() => setExpanded(!expanded)}
        title={tooltipText}
        style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 34, height: 34, borderRadius: '50%',
          cursor: 'pointer', flexShrink: 0,
          border: `1px solid ${accent}`,
          backgroundColor: `${accent}12`,
          color: accent,
          transition: 'all 0.15s',
        }}
      >
        <Calendar size={16} />
        {activeFollowups.length > 1 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            minWidth: 16, height: 16, padding: '0 4px',
            borderRadius: 8, fontSize: 10, fontWeight: 700,
            backgroundColor: accent, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `2px solid ${colors.bgPrimary}`,
          }}>
            {activeFollowups.length}
          </span>
        )}
      </button>

      {/* Expanded Panel */}
      {expanded && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          width: '320px', maxHeight: '360px', overflowY: 'auto',
          backgroundColor: colors.bgPrimary, borderRadius: '12px',
          border: `1px solid ${colors.borderColor}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          zIndex: 100, padding: '12px',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '10px',
          }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
              Agendamentos ativos
            </span>
            <button
              onClick={() => setExpanded(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary, padding: '2px' }}
            >
              <X size={14} />
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {activeFollowups.map(fu => {
              const overdue = isOverdue(fu);
              const skipped = isSkipped(fu);
              const outcomeText = describeOutcome(fu.last_outcome);
              return (
              <div key={fu.id} style={{
                padding: '10px', borderRadius: '8px',
                backgroundColor: colors.bgSecondary,
                border: `1px solid ${overdue ? OVERDUE + '66' : colors.borderColor}`,
              }}>
                <div style={{
                  fontSize: '13px', fontWeight: 500, color: colors.textPrimary,
                  marginBottom: '4px', display: 'flex', alignItems: 'center', gap: 6,
                  justifyContent: 'space-between',
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {fu.name || 'Sem nome'}
                  </span>
                  {(overdue || skipped) && (
                    <span style={{
                      flexShrink: 0, fontSize: 10, fontWeight: 700,
                      color: OVERDUE, backgroundColor: `${OVERDUE}1f`,
                      borderRadius: 6, padding: '2px 6px',
                    }}>
                      {overdue ? 'Vencido' : 'Não enviado'}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '11px', color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Repeat size={10} /> {buildScheduleText(fu.schedule)}
                  </span>
                  <span style={{ fontSize: '11px', color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Clock size={10} /> {fu.message_count || 0} mensagen{(fu.message_count || 0) !== 1 ? 's' : ''}
                  </span>
                  {outcomeText && (
                    <span style={{ fontSize: '11px', color: OVERDUE, lineHeight: 1.35 }}>
                      {outcomeText}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={() => handleRemove(fu.id)}
                    disabled={removing === fu.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '4px',
                      padding: '4px 10px', borderRadius: '6px', fontSize: '11px',
                      background: 'none', border: '1px solid #ef444430',
                      color: '#ef4444', cursor: 'pointer',
                      opacity: removing === fu.id ? 0.5 : 1,
                    }}
                  >
                    {removing === fu.id ? <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={10} />}
                    Cancelar
                  </button>
                  {onNavigateToFollowup && (
                    <button
                      onClick={() => { onNavigateToFollowup(fu.id); setExpanded(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px',
                        padding: '4px 10px', borderRadius: '6px', fontSize: '11px',
                        background: 'none', border: `1px solid ${colors.borderColor}`,
                        color: colors.textSecondary, cursor: 'pointer',
                      }}
                    >
                      <ExternalLink size={10} /> Ver detalhes
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowupBadge;
